import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TextInput, VisualOptions } from "@/components/TextInput";
import { OutputDisplay } from "@/components/OutputDisplay";
import { Navigation } from "@/components/Navigation";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Wand2, RefreshCw, Download, Menu, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingCheck } from "@/hooks/useOnboardingCheck";
import { useStreamingHumanizer } from "@/hooks/useStreamingHumanizer";
import { useStreamingVisualCreator } from "@/hooks/useStreamingVisualCreator";
import { HeroSection } from "@/components/home/HeroSection";
import { HowItWorksSection } from "@/components/home/HowItWorksSection";
import { ReferralTracker } from "@/lib/referralTracker";

// Lazy load heavy sections for better performance
const LazyHomeSections = lazy(() => import("@/components/home/LazyHomeSections").then(module => ({
  default: module.LazyHomeSections
})));
const UpgradeDialog = lazy(() => import("@/components/UpgradeDialog"));
const Index = () => {
  const {
    user
  } = useAuth();
  const {
    onboardingCompleted,
    loading: onboardingLoading
  } = useOnboardingCheck();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    streamingState,
    streamHumanizeText,
    reset: resetStreaming
  } = useStreamingHumanizer();
  const {
    streamingState: visualStreamingState,
    streamGenerateVisual,
    reset: resetVisualStreaming
  } = useStreamingVisualCreator();
  const [isLoading, setIsLoading] = useState(false);
  const [originalText, setOriginalText] = useState("");
  const [humanizedText, setHumanizedText] = useState("");
  const [visualContent, setVisualContent] = useState<{
    svg: string | null;
    dataUrl: string | null;
  }>({
    svg: null,
    dataUrl: null
  });
  const [visualVersions, setVisualVersions] = useState<Array<{
    id: string;
    svg: string;
    dataUrl: string | null;
    label: string;
    timestamp: number;
  }>>([]);
  const [includeVisuals, setIncludeVisuals] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true);
  const [currentStep, setCurrentStep] = useState<'text' | 'visuals' | 'complete'>('text');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check for pending text after login and auto-start processing
  useEffect(() => {
    const checkPendingText = async () => {
      if (user) {
        const pendingText = localStorage.getItem('pendingHumanizeText');
        const pendingVisuals = localStorage.getItem('pendingHumanizeVisuals');
        const pendingOptions = localStorage.getItem('pendingHumanizeOptions');
        const pendingIntensity = localStorage.getItem('pendingHumanizeIntensity');
        if (pendingText) {
          // Clear pending data
          localStorage.removeItem('pendingHumanizeText');
          localStorage.removeItem('pendingHumanizeVisuals');
          localStorage.removeItem('pendingHumanizeOptions');
          localStorage.removeItem('pendingHumanizeIntensity');

          // Auto-start processing
          toast.success("Welcome! Starting text humanization...");
          setTimeout(async () => {
            await handleTextSubmit(pendingText, pendingVisuals === 'true', pendingOptions ? JSON.parse(pendingOptions) : undefined, pendingIntensity || 'medium');
          }, 1500); // Delay to let the user see they're logged in
        }
      }
    };
    checkPendingText();
  }, [user]);

  // Capture referral data on initial visit
  useEffect(() => {
    ReferralTracker.capture();
  }, []);

  // Load project for editing from sessionStorage or query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'edit') {
      // Prefer sessionStorage payload to avoid URL length issues
      const payloadRaw = sessionStorage.getItem('editProjectPayload');
      let original = '';
      let humanized = '';
      let visuals: any[] = [];
      if (payloadRaw) {
        try {
          const payload = JSON.parse(payloadRaw);
          original = payload.originalText || '';
          humanized = payload.humanizedText || '';
          visuals = Array.isArray(payload.extractedVisuals) ? payload.extractedVisuals : [];
        } catch {}
      } else {
        // Fallback to query params
        original = params.get('originalText') || '';
        humanized = params.get('humanizedText') || '';
        const visualsParam = params.get('extractedVisuals') || '[]';
        try {
          visuals = JSON.parse(visualsParam);
        } catch {}
      }
      setOriginalText(original);
      setHumanizedText(humanized);
      if (Array.isArray(visuals) && visuals.length > 0 && visuals[0]?.svg) {
        setIncludeVisuals(true);
        // Convert all extractedVisuals to VisualVersion format
        const visualVersions = visuals.map((visual: any, index: number) => ({
          id: visual.id || `v${index}-${Date.now()}`,
          svg: visual.svg,
          dataUrl: visual.dataUrl || `data:image/svg+xml;utf8,${encodeURIComponent(visual.svg)}`,
          label: `v${index}`,
          timestamp: visual.created_at ? new Date(visual.created_at).getTime() : Date.now() + index
        }));
        setVisualVersions(visualVersions);
        setVisualContent({
          svg: visuals[0].svg,
          dataUrl: visuals[0].dataUrl || null
        });
      } else {
        setIncludeVisuals(false);
        setVisualContent({
          svg: null,
          dataUrl: null
        });
        setVisualVersions([]);
      }
      setCurrentStep('complete');

      // Clean up
      sessionStorage.removeItem('editProjectPayload');
      window.history.replaceState({}, document.title, window.location.pathname);
      toast.success('Loaded project for editing');
    }
  }, [location.search]);
  const saveHumanizedContent = async (text: string, originalPrompt: string) => {
    if (!user) return;
    try {
      // Generate a consistent series_id for this content
      const seriesId = crypto.randomUUID();
      const {
        error
      } = await supabase.from('content_items').insert({
        user_id: user.id,
        kind: 'humanized',
        series_id: seriesId,
        version: 1,
        title: `Humanized Text`,
        prompt: originalPrompt,
        data: {
          text: text,
          originalText: originalPrompt
        }
      });
      if (error) throw error;
      toast.success("Text saved to My Content!");
    } catch (error) {
      console.error('Error saving humanized text:', error);
      toast.error("Failed to save text");
    }
  };
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const saveCompleteProject = async (originalText: string, humanizedText: string, visuals: Array<{
    svg: string;
    dataUrl?: string | null;
  }> = []) => {
    if (!user) return null;
    try {
      const versionedVisuals = visuals.map(v => ({
        version: 1,
        svg: v.svg,
        dataUrl: v.dataUrl ?? null,
        created_at: new Date().toISOString()
      }));

      // Generate a consistent series_id for this project
      const seriesId = crypto.randomUUID();
      const {
        data,
        error
      } = await supabase.from('content_items').insert({
        user_id: user.id,
        kind: 'humanized',
        series_id: seriesId,
        title: 'Humanized Project',
        prompt: originalText,
        version: 1,
        data: {
          originalText,
          humanizedText,
          extractedVisuals: versionedVisuals
        }
      }).select('id').single();
      if (error) throw error;
      toast.success('Project saved to My Content!');
      return data.id;
    } catch (error) {
      console.error('Error saving complete project:', error);
      toast.error('Failed to save project');
      return null;
    }
  };
  const updateProjectWithVisuals = async (projectId: string, visuals: Array<{
    svg: string;
    dataUrl?: string | null;
  }>) => {
    if (!user) return;
    try {
      // 1) Load existing project data
      const {
        data: existing,
        error: fetchError
      } = await supabase.from('content_items').select('data, version, series_id').eq('id', projectId).eq('user_id', user.id).maybeSingle();
      if (fetchError) throw fetchError;
      if (!existing) {
        throw new Error('Project not found');
      }
      const existingData = (existing as any)?.data || {};
      const existingVisuals = Array.isArray(existingData.extractedVisuals) ? existingData.extractedVisuals : [];
      const nextVersionNumber = (existingVisuals.length || 0) + 1;

      // 2) Append new visuals as a new version entry
      const newVisualEntries = visuals.map(v => ({
        version: nextVersionNumber,
        svg: v.svg,
        dataUrl: v.dataUrl ?? null,
        created_at: new Date().toISOString()
      }));
      const mergedData = {
        ...existingData,
        extractedVisuals: [...existingVisuals, ...newVisualEntries]
      };

      // 3) Update project with merged data and bump version
      const {
        error: updateError
      } = await supabase.from('content_items').update({
        data: mergedData,
        version: ((existing as any)?.version ?? 0) + 1,
        updated_at: new Date().toISOString()
      }).eq('id', projectId).eq('user_id', user.id);
      if (updateError) throw updateError;
      console.log(`Successfully updated project ${projectId} with version ${nextVersionNumber} visuals`);
      toast.success(`Project updated with version ${nextVersionNumber} visuals!`);
    } catch (error) {
      console.error('Error updating project with visuals:', error);
      toast.error('Failed to update project with new visuals');
    }
  };
  const processHumanization = async (text: string, visuals: boolean, visualOptions?: VisualOptions, intensity?: string, format?: string) => {
    if (useStreaming) {
      // Step 1: Start with text humanization
      setCurrentStep('text');
      setOriginalText(text);
      setIncludeVisuals(visuals);
      setMobilePreview(visualOptions?.mobilePreview || false);
      setHumanizedText('');
      setVisualContent({
        svg: null,
        dataUrl: null
      });
      try {
        // First stream the humanized text (without visuals)
        const result = await streamHumanizeText(text, false, undefined, intensity, format);
        if (result) {
          setHumanizedText(result);

          // Always auto-save text immediately when it completes
          let savedProjectId: string | null = null;
          if (user) {
            savedProjectId = await saveCompleteProject(text, result, []);
            if (savedProjectId) {
              setCurrentProjectId(savedProjectId);
              // Store project ID in sessionStorage for cross-page access
              sessionStorage.setItem('currentProjectId', savedProjectId);
            }
          }
          toast.success("Text humanized and saved!");

          // Step 2: If visuals requested, generate them after text is complete
          if (visuals) {
            setCurrentStep('visuals');
            toast.info("Now generating visual explanations...");
            try {
              const visualResult = await streamGenerateVisual(result, visualOptions?.style || 'auto');
              if (visualResult?.svg && visualResult?.dataUrl) {
                setVisualContent({
                  svg: visualResult.svg,
                  dataUrl: visualResult.dataUrl
                });
                // Use the immediate project ID instead of state to avoid timing issues
                if (savedProjectId) {
                  await updateProjectWithVisuals(savedProjectId, [{
                    svg: visualResult.svg,
                    dataUrl: visualResult.dataUrl
                  }]);
                  toast.success("Visual explanations generated and saved!");
                } else {
                  console.error('No project ID available to update with visuals');
                  toast.error('Failed to save visuals to project');
                }
              }
              setCurrentStep('complete');
            } catch (visualError) {
              console.error("Error generating visuals:", visualError);
              toast.error("Failed to generate visuals, but text was humanized successfully");
              setCurrentStep('complete');
            }
          } else {
            setCurrentStep('complete');
          }
        }
      } catch (error) {
        console.error("Error streaming humanization:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to humanize text";
        if (errorMessage.includes('No active subscription') || errorMessage.includes('No credits remaining')) {
          setShowUpgradeDialog(true);
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
        setCurrentStep('complete');
      }
    } else {
      // Use original non-streaming approach
      setIsLoading(true);
      setOriginalText(text);
      setIncludeVisuals(visuals);
      setMobilePreview(visualOptions?.mobilePreview || false);
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke('humanize-text', {
          body: {
            text,
            includeVisuals: visuals,
            visualOptions,
            intensity
          }
        });
        if (error) {
          throw error;
        }
        if (!data || !data.result) {
          throw new Error('No result returned from humanization service');
        }
        setHumanizedText(data.result);

        // Dispatch credits update event
        if (data.credits_remaining !== undefined) {
          window.dispatchEvent(new CustomEvent('creditsUpdated', {
            detail: {
              credits_remaining: data.credits_remaining
            }
          }));
        }
        toast.success("Text humanized successfully!");
      } catch (error) {
        console.error("Error humanizing text:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to humanize text";
        if (errorMessage.includes('No active subscription') || errorMessage.includes('No credits remaining')) {
          setShowUpgradeDialog(true);
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };
  const handleTextSubmit = async (text: string, visuals: boolean, visualOptions?: VisualOptions, intensity?: string, format?: string) => {
    if (!user) {
      // Save text for after registration and redirect to auth
      localStorage.setItem('pendingHumanizeText', text);
      localStorage.setItem('pendingHumanizeVisuals', visuals.toString());
      localStorage.setItem('pendingHumanizeOptions', JSON.stringify(visualOptions));
      localStorage.setItem('pendingHumanizeIntensity', intensity || 'medium');
      toast.info("Please register to humanize your text - your content will be saved!");
      navigate('/auth');
      return;
    }

    // Pre-check credits before invoking the edge function
    try {
      const {
        data: sub,
        error
      } = await supabase.from('user_subscriptions').select('credits_remaining, status').eq('user_id', user.id).eq('status', 'active').maybeSingle();
      if (error) {
        console.error('Error fetching subscription:', error);
      }
      if (!sub || sub.credits_remaining !== null && sub.credits_remaining <= 0) {
        setShowUpgradeDialog(true);
        toast.error("You're out of credits. Please upgrade to continue.");
        return;
      }
    } catch (e) {
      console.error('Subscription pre-check failed:', e);
    }
    await processHumanization(text, visuals, visualOptions, intensity, format);
  };
  const resetApp = () => {
    setOriginalText("");
    setHumanizedText("");
    toast.info("App reset successfully");
  };
  const startOver = () => {
    setOriginalText("");
    setHumanizedText("");
    setVisualContent({
      svg: null,
      dataUrl: null
    });
    setCurrentStep('text');
    setCurrentProjectId(null);
    sessionStorage.removeItem('currentProjectId'); // Clear project context
    resetStreaming();
    resetVisualStreaming();
  };
  return <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                
                <p className="text-sm text-muted-foreground">
                  Transform AI content into natural, human-like writing
                </p>
              </div>
            </Link>
            
            {/* Mobile menu button */}
            <div className="sm:hidden">
              <Button variant="ghost" size="sm" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-primary hover:text-primary hover:bg-primary/10">
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
            
            {/* Desktop navigation */}
            <div className="hidden sm:flex items-center gap-3">
              {humanizedText && <Button variant="secondary" size="sm" onClick={startOver} className="flex items-center gap-2 bg-primary/10 hover:bg-primary/20 text-primary border-primary/30 transition-all duration-200">
                  <RefreshCw className="w-4 h-4" />
                  New Text
                </Button>}
              
              {user && <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200 font-medium">
                  <Link to="/my-content" className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    My Content
                  </Link>
                </Button>}
              
              <Button variant="ghost" size="sm" onClick={() => {
              const inputSection = document.getElementById('humanizer-input');
              if (inputSection) {
                inputSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
            }} className="text-primary hover:text-primary hover:bg-primary/10 transition-all duration-200 font-medium cursor-pointer">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4" />
                  Text Humanizer
                </div>
              </Button>
              
              <Navigation />
            </div>
          </div>
          
          {/* Mobile navigation menu */}
          {isMobileMenuOpen && <div className="sm:hidden mt-4 pt-4 border-t border-border">
              <div className="flex flex-col gap-3">
                {user && <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200 font-medium justify-start" onClick={() => setIsMobileMenuOpen(false)}>
                    <Link to="/my-content" className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      My Content
                    </Link>
                  </Button>}
                
                <Button variant="ghost" size="sm" onClick={() => {
              const inputSection = document.getElementById('humanizer-input');
              if (inputSection) {
                inputSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
              setIsMobileMenuOpen(false);
            }} className="text-primary hover:text-primary hover:bg-primary/10 transition-all duration-200 font-medium cursor-pointer justify-start">
                  <div className="flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />
                    Text Humanizer
                  </div>
                </Button>
                
                {humanizedText && <Button variant="secondary" size="sm" onClick={() => {
              startOver();
              setIsMobileMenuOpen(false);
            }} className="flex items-center gap-2 bg-secondary/50 hover:bg-secondary transition-all duration-200 justify-start">
                  <RefreshCw className="w-4 h-4" />
                  New Text
                </Button>}
                
                <div className="w-full">
                  <Navigation />
                </div>
              </div>
            </div>}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user ?
      // Logged in user - show only input and output
      <div className="max-w-6xl mx-auto">
            {!humanizedText ? <div id="humanizer-input">
                <TextInput onSubmit={handleTextSubmit} isLoading={isLoading || streamingState.isStreaming || visualStreamingState.isGenerating || currentStep === 'visuals'} />
              </div> : <OutputDisplay originalText={originalText} humanizedText={streamingState.isStreaming ? streamingState.content : humanizedText} includeVisuals={includeVisuals} mobilePreview={mobilePreview} isStreaming={streamingState.isStreaming} visualContent={visualContent} visualVersions={visualVersions} isGeneratingVisuals={visualStreamingState.isGenerating} currentStep={currentStep} onContentUpdate={(newContent, type) => {
          if (type === 'text') {
            setHumanizedText(newContent);
          } else if (type === 'visual') {
            setVisualContent({
              svg: newContent,
              dataUrl: null
            });
          }
        }} />}
          </div> :
      // Not logged in - show full home page
      <div className="max-w-6xl mx-auto">
            <HeroSection onScrollToInput={() => {
          const inputSection = document.getElementById('humanizer-input');
          if (inputSection) {
            inputSection.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }} />

            <div id="humanizer-input">
              <TextInput onSubmit={handleTextSubmit} isLoading={isLoading || streamingState.isStreaming || visualStreamingState.isGenerating || currentStep === 'visuals'} />
            </div>

            <HowItWorksSection />
            
            {/* Lazy-loaded heavy sections for better performance */}
            <Suspense fallback={<div className="h-20 flex items-center justify-center"><div className="animate-pulse bg-muted rounded-lg h-8 w-48"></div></div>}>
              <LazyHomeSections />
            </Suspense>
          </div>}
      </main>


      {/* Lazy-loaded upgrade dialog */}
      <Suspense fallback={null}>
        <UpgradeDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog} />
      </Suspense>
    </div>;
};
export default Index;